// Pages API client — wired to the real backend (Sprint 7 — Agent P).
//
// All paths use the `/api/v1/pages/...` prefix on top of the configured
// `http` axios instance (baseURL = http://localhost:5001 by default).
//
// Endpoints return `ActionResponse<T>` envelopes and are unwrapped here so
// callers receive clean typed values.

import { http, unwrap, unwrapCreatedId, unwrapList, type ActionResponse } from './http';
import type { PaginationInfo } from './http';
import {
  pageStatusFromNumber,
  pageStatusToNumber,
  pageTypeFromNumber,
  pageTypeToNumber,
  type PageListItem,
  type PageListQuery,
  type PageVersion,
  type PageWriteInput,
  type StaticPage,
} from '@features/pages/types';

// ---- Raw BE DTO shapes (mirror swagger) ------------------------------------

interface PageDto {
  id: string;
  title: string | null;
  slug: string | null;
  pageType: number;
  body: string | null;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  keywords: string | null;
  allowIndexing: boolean;
  status: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
  hreflangAlternates?: Array<{ languageCode: string | null; slug: string | null }> | null;
}

interface PageListItemDto {
  id: string;
  title: string | null;
  slug: string | null;
  pageType: number;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  status: number;
  publishedAt: string | null;
  updatedAt: string;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface PageVersionDto {
  id: string;
  pageId: string;
  versionNumber: number;
  title: string | null;
  slug: string | null;
  pageType: number;
  body: string | null;
  excerpt: string | null;
  coverImageUrl: string | null;
  sortOrder: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

// ---- Mappers ---------------------------------------------------------------

function mapPage(d: PageDto): StaticPage {
  return {
    id: d.id,
    title: d.title ?? '',
    slug: d.slug ?? '',
    pageType: pageTypeFromNumber(d.pageType),
    body: d.body ?? '',
    excerpt: d.excerpt,
    coverImageUrl: d.coverImageUrl,
    sortOrder: d.sortOrder ?? 0,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    keywords: d.keywords ?? null,
    allowIndexing: d.allowIndexing ?? true,
    status: pageStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
    hreflangAlternates: (d.hreflangAlternates ?? []).map((h) => ({
      languageCode: h.languageCode ?? '',
      slug: h.slug ?? '',
    })),
  };
}

function mapListItem(d: PageListItemDto): PageListItem {
  return {
    id: d.id,
    title: d.title ?? '',
    slug: d.slug ?? '',
    pageType: pageTypeFromNumber(d.pageType),
    excerpt: d.excerpt,
    coverImageUrl: d.coverImageUrl,
    sortOrder: d.sortOrder ?? 0,
    status: pageStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    updatedAt: d.updatedAt,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapVersion(d: PageVersionDto): PageVersion {
  return {
    id: d.id,
    pageId: d.pageId,
    versionNumber: d.versionNumber,
    title: d.title ?? '',
    slug: d.slug ?? '',
    pageType: pageTypeFromNumber(d.pageType),
    body: d.body ?? '',
    excerpt: d.excerpt,
    coverImageUrl: d.coverImageUrl,
    sortOrder: d.sortOrder ?? 0,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    allowIndexing: d.allowIndexing ?? true,
    authorUserId: d.authorUserId,
    authorEmail: d.authorEmail,
    changeNote: d.changeNote,
    createdAt: d.createdAt,
  };
}

function toCreatePayload(input: PageWriteInput) {
  return {
    title: input.title,
    body: input.body,
    pageType: pageTypeToNumber(input.pageType),
    slug: input.slug ?? null,
    excerpt: input.excerpt ?? null,
    coverImageUrl: input.coverImageUrl ?? null,
    sortOrder: input.sortOrder ?? 0,
    metaTitle: input.metaTitle ?? null,
    metaDescription: input.metaDescription ?? null,
    canonicalTag: input.canonicalTag ?? null,
    customCode: input.customCode ?? null,
    keywords: input.keywords ?? null,
    allowIndexing: input.allowIndexing ?? true,
    changeNote: input.changeNote ?? null,
    // İçerik i18n — yalnız create'te (varyant) gönderilir; update'te set edilmez.
    ...(input.languageId ? { languageId: input.languageId } : {}),
    ...(input.groupId ? { groupId: input.groupId } : {}),
  };
}

// ---- Public API ------------------------------------------------------------

export interface PageListResult {
  items: PageListItem[];
  pagination: PaginationInfo | null;
  total: number;
}

export const pagesApi = {
  async list(query: PageListQuery = {}): Promise<PageListResult> {
    const p = new URLSearchParams();
    if (query.search) p.set('search', query.search);
    if (query.pageType) p.set('pageType', String(pageTypeToNumber(query.pageType)));
    if (query.status) p.set('status', String(pageStatusToNumber(query.status)));
    if (query.publishedOnly) p.set('publishedOnly', 'true');
    if (query.includeDeleted) p.set('includeDeleted', 'true');
    if (query.languageId) p.set('languageId', query.languageId);
    if (query.page) p.set('page', String(query.page));
    if (query.pageSize) p.set('pageSize', String(query.pageSize));
    if (query.sortBy) p.set('sortBy', query.sortBy);
    if (query.descending === false) p.set('descending', 'false');

    const qs = p.toString();
    const res = await http.get<ActionResponse<PageListItemDto>>(
      `/api/v1/pages${qs ? `?${qs}` : ''}`,
    );
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapListItem),
      pagination,
      total: pagination?.totalCount ?? items.length,
    };
  },

  async get(id: string): Promise<StaticPage> {
    const res = await http.get<ActionResponse<PageDto>>(`/api/v1/pages/${id}`);
    return mapPage(unwrap(res));
  },

  /** Anonymous: only Published pages are returned. */
  async getBySlug(slug: string): Promise<StaticPage> {
    const res = await http.get<ActionResponse<PageDto>>(
      `/api/v1/pages/public/${encodeURIComponent(slug)}`,
    );
    return mapPage(unwrap(res));
  },

  /** Create returns the BE-generated GUID (BE may answer with a bare id or the full entity). */
  async create(input: PageWriteInput): Promise<string> {
    const res = await http.post<ActionResponse<string | { id: string }>>(
      '/api/v1/pages',
      toCreatePayload(input),
    );
    return unwrapCreatedId(res);
  },

  async update(id: string, input: PageWriteInput): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(
      `/api/v1/pages/${id}`,
      toCreatePayload(input),
    );
    unwrap(res);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`/api/v1/pages/${id}`);
    unwrap(res);
  },

  async submitReview(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/pages/${id}/submit-review`,
    );
    unwrap(res);
  },

  /** Approve = move InReview → Approved; pass `approve=false` to reject. */
  async approve(id: string, approve = true, note?: string): Promise<void> {
    const p = new URLSearchParams();
    p.set('approve', approve ? 'true' : 'false');
    if (note) p.set('note', note);
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/pages/${id}/approve?${p.toString()}`,
    );
    unwrap(res);
  },

  async publish(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/pages/${id}/publish`);
    unwrap(res);
  },

  async archive(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/pages/${id}/archive`);
    unwrap(res);
  },

  async listVersions(id: string): Promise<PageVersion[]> {
    const res = await http.get<ActionResponse<PageVersionDto>>(
      `/api/v1/pages/${id}/versions`,
    );
    const { items } = unwrapList(res);
    return items.map(mapVersion);
  },

  async restoreVersion(id: string, versionNumber: number): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/pages/${id}/versions/${versionNumber}/restore`,
    );
    unwrap(res);
  },
};

export type { StaticPage, PageListItem, PageVersion } from '@features/pages/types';
